import { Link } from "react-router-dom";
import lfLogoFull from "@/assets/logos/lf-logo-full.svg";
import lfSignetWhite from "@/assets/logos/lf-signet-white.svg";
import ImageLightbox from "@/components/ImageLightbox";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Users,
  TrendingUp,
  BookOpen,
  Target,
  Quote,
  MessageCircle,
  Eye,
  GraduationCap,
  Heart,
  Briefcase,
  ExternalLink,
  Download,
} from "lucide-react";
import OpenAIIcon from "@/components/icons/OpenAIIcon";
import bogenBg from "@/assets/bogen-bg.png";
import teamHero from "@/assets/team-hero.jpg";
import teamHaltung from "@/assets/team-haltung.jpg";
import teamHeroSitting from "@/assets/team-hero-sitting.jpg";
import burkhardStephanConversation from "@/assets/burkhard-stephan-conversation.jpg";
import certAusbildungsinstitut from "@/assets/cert-ausbildungsinstitut.png";
import certDvct from "@/assets/cert-dvct.jpg";
import certEtrainerin from "@/assets/cert-etrainerin.png";
import certDgsv from "@/assets/cert-dgsv.jpg";
import certZortify from "@/assets/cert-zortify.jpg";

// Company logos
import logoSosKinderdorf from "@/assets/logos/sos-kinderdorf.png";
import logoTuUlm from "@/assets/logos/tu-ulm.svg";
import logoTng from "@/assets/logos/tng.svg";
import logoCaritas from "@/assets/logos/caritas.png";
import logoTum from "@/assets/logos/tum.png";
import logoBp from "@/assets/logos/bp.svg";
import logoEkd from "@/assets/logos/ekd.png";
import logoSparkasse from "@/assets/logos/sparkasse.jpg";
import logoBusinessforce from "@/assets/logos/businessforce.svg";
import { partners } from "@/data/partners";
import logoKreuzwerker from "@/assets/logos/kreuzwerker.jpg";
import logoDrWalter from "@/assets/logos/dr-walter.svg";
import logoHwz from "@/assets/logos/hwz.png";
import logoHensoldt from "@/assets/logos/hensoldt.png";
import logoEsg from "@/assets/logos/esg.png";

import { teamMembers } from "@/data/teamMembers";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { FEATURE_FLAGS, CHATBOT_URL } from "@/config/featureFlags";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import MarqueeTicker from "@/components/MarqueeTicker";
import { useLocation } from "react-router-dom";
import BlogCard from "@/components/BlogCard";
import { getAllPosts } from "@/lib/blog";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness"],
  name: "Leadership Focus GmbH",
  url: "https://leadership-focus.de",
  logo: "https://leadership-focus.de/favicon.ico",
  description:
    "Leadership Focus begleitet Führungskräfte und Organisationen mit Coaching, Beratung, Supervision und Qualifizierung – neurowissenschaftlich fundiert und systemisch.",
  foundingDate: "2008",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Leopoldstr. 77",
    addressLocality: "München",
    postalCode: "80802",
    addressCountry: "DE",
  },
  location: [
    {
      "@type": "Place",
      name: "Leadership Focus – München",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Leopoldstr. 77",
        addressLocality: "München",
        postalCode: "80802",
        addressCountry: "DE",
      },
    },
    {
      "@type": "Place",
      name: "Leadership Focus – Erlangen",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Erlangen",
        addressCountry: "DE",
      },
    },
  ],
  email: "kontakt@leadership-focus.de",
sameAs: [
    "https://www.linkedin.com/in/margret-klinkhammer-16841239/",
    "https://www.linkedin.com/in/stephanrathgeber/",
    "https://www.linkedin.com/in/burkhard-rathgeber/"
  ],
  founder: {
    "@type": "Person",
    name: "Dr. Margret Klinkhammer",
    jobTitle: "Geschäftsführerin",
  },
  areaServed: [
    { "@type": "Country", name: "Deutschland" },
    { "@type": "Country", name: "Österreich" },
    { "@type": "Country", name: "Schweiz" },
  ],
  knowsAbout: [
    "Executive Coaching",
    "Organisationsberatung",
    "Supervision",
    "Führungskräfteentwicklung",
    "Change Management",
    "Neurowissenschaftlich-systemische Beratung",
  ],
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

// ── Hero Data ──
const services = [
  {
    icon: Users,
    title: "Coaching & Supervision",
    description:
      "Individuelle Begleitung für Führungskräfte und Teams zur persönlichen und professionellen Weiterentwicklung.",
  },
  {
    icon: TrendingUp,
    title: "Organisationsberatung",
    description: "Strategische Beratung und Begleitung von Veränderungsprozessen in Organisationen.",
  },
  {
    icon: BookOpen,
    title: "Aus- & Fortbildungen",
    description: "Qualifizierungsprogramme und Workshops für nachhaltige Kompetenzentwicklung.",
  },
  {
    icon: Target,
    title: "Strategieentwicklung",
    description: "Gemeinsam erarbeiten wir klare Strategien für die Zukunft Ihrer Organisation.",
  },
];

const stats = [
  { value: "50+", label: "Jahre Organisationsgestaltung" },
  { value: "80+", label: "Begleitete Kunden" },
  { value: "600+", label: "Workshops, Seminare & Coachingprozesse" },
  { value: "12", label: "Führungs- und Veränderungsprogramme" },
];

// ── Portfolio Data ──
const portfolioItems = [
  {
    icon: MessageCircle,
    title: "Beratung",
    description: "Strategische Beratung und Begleitung von Veränderungsprozessen in Organisationen.",
    href: "/portfolio/beratung",
  },
  {
    icon: BookOpen,
    title: "Qualifizierung",
    description: "Qualifizierungsprogramme und Workshops für nachhaltige Kompetenzentwicklung.",
    href: "/portfolio/qualifizierung",
  },
  {
    icon: Eye,
    title: "Coaching",
    description: "Individuelle Begleitung und Entwicklung für Führungskräfte und Teams.",
    href: "/portfolio/coaching",
  },
  {
    icon: Users,
    title: "Supervision",
    description: "Professionelle Reflexion und Begleitung für Fachkräfte und Teams.",
    href: "/portfolio/supervision",
  },
];

// teamMembers imported from @/data/teamMembers

// ── Section Divider ──
const SectionDivider = () => (
  <div className="w-full flex justify-center py-2">
    <div className="w-24 h-px bg-border" />
  </div>
);

const Index = () => {
  const [selectedReferenz, setSelectedReferenz] = useState<{
    person: string;
    schlagworte: string;
    vollzitat?: string;
  } | null>(null);
  const location = useLocation();

  const latestPosts = getAllPosts().slice(0, 3);

  // Handle hash scrolling on mount and hash change
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location.hash]);

  return (
    <>
      <SEOHead
        title="Leadership Focus – Coaching, Beratung & Organisationsentwicklung München"
        description="Leadership Focus begleitet Führungskräfte und Organisationen mit Coaching, Beratung, Supervision und Qualifizierung. Neurowissenschaftlich fundiert, seit 2008 in München."
        canonical="https://leadership-focus.de/"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Header />
      <main>
        {/* ═══════════════════════════════════════════════════════ */}
        {/* HERO / HOME */}
        {/* ═══════════════════════════════════════════════════════ */}
        <section
          id="home"
          className="relative overflow-hidden bg-gradient-to-br from-primary-light via-background to-background"
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--primary)/0.08),transparent_70%)]" />
          <img
            src={bogenBg}
            alt=""
            aria-hidden="true"
            className="absolute top-0 right-0 w-[90%] md:w-[72%] lg:w-[65%] h-auto opacity-90 pointer-events-none select-none z-0"
          />
          <div className="container relative py-12 md:py-16 lg:py-20">
            <div className="flex flex-col lg:flex-row items-stretch gap-10 lg:gap-16">
              {/* Text – left */}
              <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex-1 min-w-0 flex flex-col justify-center">
                <motion.div variants={fadeInUp} className="mb-10 -mt-4 md:-mt-20 lg:-mt-40">
                  <img src={lfLogoFull} alt="Leadership Focus" className="h-16 md:h-20 lg:h-24 w-auto" />
                </motion.div>
                <motion.h1
                  variants={fadeInUp}
                  className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight text-foreground mb-6"
                >
                  Führung stärken.
                  <span className="text-primary"> Organisationen</span> entwickeln.
                </motion.h1>
                <motion.p
                  variants={fadeInUp}
                  className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl"
                >
                  Wir begleiten Führungskräfte, Mitarbeitende und Organisationen auf dem Weg zu erfolgreichem und nachhaltigem Wandel –
                  mit Erfahrung, Empathie, Künstlicher Intelligenz und wissenschaftlicher Fundierung.
                </motion.p>
                <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" className="text-base px-8">
                    <a href="#portfolio">Lösungen</a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="text-base px-8">
                    <a href="mailto:kontakt@leadership-focus.de">kontakt@leadership-focus.de</a>
                  </Button>
                </motion.div>
              </motion.div>

              {/* Team photo – right, flush with section top & bottom */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="flex-1 min-w-0 w-full lg:max-w-[62%] lg:-ml-12 lg:-mr-[4rem] relative mt-8 -mb-16 md:-my-24 lg:-my-32"
              >
                {/* Decorative green circle */}
                <div
                  className="absolute -right-12 top-1/2 -translate-y-1/2 w-[350px] h-[350px] md:w-[450px] md:h-[450px] rounded-full pointer-events-none"
                  style={{ backgroundColor: 'hsl(var(--primary) / 0.12)' }}
                />
                <div className="relative overflow-hidden h-full">
                  <ImageLightbox
                    src={teamHeroSitting}
                    alt="Das Leadership Focus Team – Stephan Rathgeber, Margret Klinkhammer und Burkhard Rathgeber"
                    className="w-full h-full object-cover"
                  />
                  {/* Bottom edge gradient fade */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to top, hsl(var(--background)) 0%, transparent 20%)',
                    }}
                  />
                  {/* Top edge gradient fade */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(to bottom, hsl(var(--background)) 0%, transparent 10%)',
                    }}
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Vertrauenselemente */}
        <section className="py-16 bg-white border-y border-border/50">
          <div className="container">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center text-sm font-medium text-muted-foreground uppercase tracking-widest mb-8"
            >
              Vertraut von führenden Organisationen
            </motion.p>
            <MarqueeTicker
              items={[
                { name: "SOS Kinderdorf Deutschland e.V.", logo: logoSosKinderdorf },
                { name: "TU Ulm", logo: logoTuUlm },
                { name: "TNG Technology Consulting", logo: logoTng },
                { name: "Caritas", logo: logoCaritas },
                { name: "TU München", logo: logoTum },
                { name: "BP-EMEA", logo: logoBp },
                { name: "Evangelische Kirche", logo: logoEkd },
                { name: "Sparkasse Düsseldorf", logo: logoSparkasse },
                { name: "Businessforce", logo: logoBusinessforce },
                { name: "Kreuzwerker GmbH", logo: logoKreuzwerker },
                { name: "DR-WALTER Reiseversicherung", logo: logoDrWalter },
                { name: "HWZ", logo: logoHwz },
                { name: "Hensoldt", logo: logoHensoldt },
                { name: "ESG GmbH", logo: logoEsg },
              ]}
              className="py-4"
            />
          </div>
        </section>

        {/* Kennzahlen & Zertifizierungen */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="container">
            {/* Obere Hälfte: Kennzahlen */}
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeInUp}
              className="text-sm font-semibold uppercase tracking-widest text-primary-foreground/70 mb-8 text-center"
            >
              Wir stehen für langjährige Begleitung
            </motion.p>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}
              className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-center"
            >
              {stats.map((stat) => (
                <motion.div key={stat.label} variants={fadeInUp}>
                  <div className="text-4xl md:text-5xl font-bold font-serif mb-2">{stat.value}</div>
                  <div className="text-sm text-primary-foreground/70 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* Trennlinie */}
            <div className="my-12 border-t border-primary-foreground/20" />

            {/* Untere Hälfte: Zertifizierungen */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}
              className="text-center"
            >
              <motion.p variants={fadeInUp} className="text-sm font-semibold uppercase tracking-widest text-primary-foreground/70 mb-8">
                Unsere Zertifizierungen
              </motion.p>
              <motion.div
                variants={fadeInUp}
                className="flex flex-wrap items-center justify-center gap-8 md:gap-12"
              >
                {[
                  { src: certAusbildungsinstitut, alt: "Ausbildungsinstitut für Coaching im dvct" },
                  { src: certDvct, alt: "dvct – Deutscher Verband für Coaching & Training e.V." },
                  { src: certEtrainerin, alt: "eTrainerin dvct zertifiziert" },
                  { src: certDgsv, alt: "DGSv – Deutsche Gesellschaft für Supervision und Coaching, Mitglied" },
                  { src: certZortify, alt: "Zortify – Select & Grow Certified" },
                ].map((cert) => (
                  <div key={cert.alt} className="bg-white rounded-lg p-3 shadow-sm">
                    <img src={cert.src} alt={cert.alt} className="h-16 md:h-20 w-auto object-contain" />
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        <SectionDivider />

        {/* ═══════════════════════════════════════════════════════ */}
        {/* LÖSUNGEN */}
        {/* ═══════════════════════════════════════════════════════ */}
        <section id="portfolio" className="py-20 md:py-28 bg-background scroll-mt-14">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
              className="text-center mb-14"
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm font-semibold uppercase tracking-widest text-primary mb-3"
              >
                Was wir tun
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-4">
                Lösungen
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Entdecken Sie unsere vier Kernbereiche – maßgeschneidert für Ihre Herausforderungen.
              </motion.p>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={staggerContainer}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {portfolioItems.map((item) => (
                <motion.div key={item.title} variants={fadeInUp}>
                  <Link to={item.href}>
                    <Card className="h-full border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                      <CardContent className="p-8">
                        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                          <item.icon className="h-6 w-6" />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground mb-2 font-serif">{item.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
            {FEATURE_FLAGS.showChatBot && (
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                className="mt-12"
              >
                <a
                  href={CHATBOT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-brand-magenta text-brand-magenta-foreground rounded-2xl px-8 py-10 md:px-12 md:py-12 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.01]"
                >
                  <div className="flex flex-col md:flex-row md:items-center items-center justify-between gap-6 text-center md:text-left md:pl-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 justify-center md:justify-start">
                        <img
                          src={lfSignetWhite}
                          alt="Leadership Focus Signet"
                          className="h-10 w-10 md:h-12 md:w-12 shrink-0"
                        />
                        <h3 className="text-xl md:text-2xl font-serif font-bold text-white">
                          Leadership Focus Chat – Dein Sparringspartner
                        </h3>
                      </div>
                      <p className="text-white/90 mt-4 text-sm md:text-base text-left">
                        Ich unterstütze dich dabei, Muster in deiner Führung und deiner Organisation sichtbar zu machen. Damit Du klarere Entscheidungen triffst & wirksamer handelst.
                      </p>
                    </div>
                    <div className="flex flex-col items-center gap-3 shrink-0">
                      <div className="flex items-center gap-3 text-white font-semibold group-hover:gap-4 transition-all">
                        <OpenAIIcon className="h-6 w-6 md:h-7 md:w-7 text-white shrink-0" />
                        Jetzt ausprobieren
                        <ArrowRight className="h-5 w-5" />
                      </div>
                      <p className="text-xs text-white/70 text-center max-w-[240px]">
                        *Der Link führt direkt zu OpenAI als externe Domain. Ein ChatGPT-Account wird benötigt.<br />Es gelten die Datenschutz- und Trainingsregelungen deines eigenen Accounts.
                      </p>
                    </div>
                  </div>
                </a>
              </motion.div>
            )}
          </div>
        </section>

        <SectionDivider />

        {/* ═══════════════════════════════════════════════════════ */}
        {/* UNSERE HALTUNG */}
        {/* ═══════════════════════════════════════════════════════ */}
        <section id="unsere-haltung" className="py-20 md:py-28 bg-background scroll-mt-14">
          <div className="container">
            {/* Headline + Subheadline oben zentriert */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
              className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm font-semibold uppercase tracking-widest text-primary mb-3"
              >
                Was uns leitet
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-6">
                Unsere Haltung
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Wir vereinen tiefgreifendes Verständnis für neurowissenschaftliche Erkenntnisse mit einer systemischen
                und menschlich zugewandten Haltung – so verstehen wir uns als Brückenbauer für nachhaltige Veränderung –
                und begleiten Sie dabei, moderne Führung langfristig wirksam werden zu lassen.
              </motion.p>
            </motion.div>

            {/* Bild links + Kernwerte rechts */}
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">
              {/* Bild */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6 }}
                className="w-full lg:w-[42%] lg:sticky lg:top-24 shrink-0"
              >
                <ImageLightbox
                  src={teamHero}
                  alt="Das Leadership Focus Team – Brückenbauer für nachhaltige Veränderung"
                  className="w-full h-auto rounded-2xl shadow-xl object-cover"
                />
              </motion.div>

              {/* Kernwerte rechts */}
              <div className="flex-1 flex flex-col gap-4">
                {[
                  {
                    title: "Brückenbauer",
                    subtitle: "Ratio & Emotion · Tradition & Wachstum · Wissenschaft & Praxis",
                    text: "Wir verstehen uns stets als Vermittler zwischen scheinbaren Gegensätzen, denn nur so gelingen Führungsaufgaben und Veränderungsprozesse dauerhaft. Wissenschaftliche Logik trifft auf menschliches Erleben. Bestehendes wertschätzen und Neues erforschen. Wir verbinden komplexe Theorien mit Hands-on Mentalität.",
                  },
                  {
                    title: "Achtsame Beharrlichkeit",
                    subtitle: "Empathisch & Zielorientiert · Intuitiv & Konzeptstark",
                    text: "Unsere Haltung in der Begleitung ist geprägt von einem Balanceakt: Achtsam im menschlichen Kontakt, aber konsequent in der Verfolgung des vereinbarten Auftrags. Hohe Intuition, die jedoch immer durch klare Konzepte und langjährige Führungserfahrung geleitet wird.",
                  },
                  {
                    title: "Verantwortungsbewusste Selbstführung",
                    subtitle: "Führen und sich-führen lassen",
                    text: "Unter dem Motto \u201EFühren und sich-führen lassen\u201C verstehen wir Führung als wechselseitigen Prozess. Diese Haltung impliziert, dass wirksames Führen im Außen immer mit einer tiefen Reflexion der eigenen inneren Haltung beginnt.",
                  },
                  {
                    title: "Transformatives Lernen",
                    subtitle: "Hilfe zur Selbsthilfe",
                    text: "Anstatt fertige Lösungen zu präsentieren, liegt unser Fokus auf der Hilfe zur Selbsthilfe. Es geht darum, \u201EDysfunktionales zu verlernen und Neues zu erlernen\u201C, um so Menschen und Organisationen in die volle Eigenwirksamkeit zu bringen.",
                  },
                  {
                    title: "Starkes wissenschaftliches Fundament",
                    subtitle: "Neurowissenschaftlich & Systemisch",
                    text: "Unser Kernansatz ist die \u201Eneurowissenschaftlich-systemische\u201C Perspektive. Veränderungsprozesse werden nicht oberflächlich betrachtet, sondern \u201Egehirngerecht\u201C gestaltet, um echte neuronale und organisationale Transformation zu ermöglichen.",
                  },
                ].map((value, i) => (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="group relative cursor-default"
                  >
                    <div className="rounded-2xl border border-border bg-card p-6 md:p-7 transition-all duration-300 group-hover:shadow-lg group-hover:border-primary/30">
                      <div className="transition-transform duration-300 group-hover:-translate-y-1">
                        <span className="text-xs font-semibold uppercase tracking-widest text-primary">
                          {value.subtitle}
                        </span>
                        <h3 className="text-lg md:text-xl font-serif font-bold text-foreground mt-1">{value.title}</h3>
                      </div>
                      <div className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-300 group-hover:grid-rows-[1fr]">
                        <div className="overflow-hidden">
                          <p className="pt-3 text-sm text-muted-foreground leading-relaxed">{value.text}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* ═══════════════════════════════════════════════════════ */}
        {/* WER WIR SIND */}
        {/* ═══════════════════════════════════════════════════════ */}
        <section id="wer-wir-sind" className="py-20 md:py-28 bg-surface scroll-mt-14">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
              className="text-center mb-14"
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm font-semibold uppercase tracking-widest text-primary mb-3"
              >
                Unser Team
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-4">
                Wer wir sind
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Leadership Focus ist seit der Gründung 2008 ein Familienunternehmen, das sich auf die Beratung von
                Menschen, Teams und Organisationen spezialisiert hat.
              </motion.p>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-6"
            >
              {[teamMembers[0], teamMembers[1], teamMembers[2]].map((m) => (
                <motion.div key={m.id} variants={fadeInUp}>
                  <Link to={`/wer-wir-sind/${m.id}`}>
                    <Card className="overflow-hidden border-border/50 shadow-md hover:shadow-lg hover:border-primary/30 transition-all duration-300 cursor-pointer group">
                      <CardContent className="p-0">
                        <div className="overflow-hidden">
                          <img
                            src={m.teamImageUrl || m.imageUrl}
                            alt={m.name}
                            className={`w-full h-72 object-cover object-[center_15%] group-hover:scale-105 transition-transform duration-500`}
                          />
                        </div>
                        <div className="p-5">
                          <h3 className="text-xl font-serif font-bold text-foreground">{m.name}</h3>
                          <p className="text-primary text-sm font-medium mt-1">{m.title}</p>
                          <p className="text-muted-foreground text-sm mt-2">{m.focus}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <SectionDivider />

        {/* ═══════════════════════════════════════════════════════ */}
        {/* VERÖFFENTLICHUNGEN */}
        {/* ═══════════════════════════════════════════════════════ */}
        <section id="blog" className="py-20 md:py-28 bg-surface scroll-mt-14">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
              className="text-center mb-14"
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm font-semibold uppercase tracking-widest text-primary mb-3"
              >
                Impulse & Gedanken
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-4">
                Veröffentlichungen
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Bücher, Podcasts und Newsletter – unsere Impulse zu Führung, Organisationsentwicklung und Coaching.
              </motion.p>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={staggerContainer}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {[
                {
                  title: "Change happens – Veränderungen gehirngerecht gestalten",
                  authors: "Margret Klinkhammer, Franz Hütter, Dirk Stoess, Lothar Wüst",
                  description:
                    "Veränderungen aus neurowissenschaftlicher Sicht – verknüpft mit Erkenntnissen der Systemtheorie und der Verhaltensökonomie. 3. aktualisierte und erweiterte Auflage 2024.",
                  coverImage: "/books/change-happens-cover.avif",
                  coverOpacity: "opacity-[0.14]",
                  coverSize: "bg-[length:100%_100%]",
                  downloadUrl: "",
                  externalUrl: "https://shop.haufe.de/prod/change-happens",
                  extraSpace: false,
                },
                {
                  title: "NEW HIRING – Ein neues Bewusstsein im Recruiting",
                  authors: "Stephan Rathgeber, Kira Niklas",
                  description:
                    "2. überarbeitete Auflage mit einem Vorwort von OMR-Gründer Philipp Westermeyer. Warum wir eine Evolution im Recruiting brauchen – und wie Unternehmen sie gestalten können.",
                  coverImage: "/books/new-hiring-cover.jpg",
                  coverOpacity: "opacity-[0.13]",
                  coverSize: "bg-cover",
                  downloadUrl: "/books/New_Hiring.pdf",
                  externalUrl: "",
                  extraSpace: false,
                },
                {
                  title: "BLUE HIRING – Zeit zum Umdenken",
                  authors: "Stephan Rathgeber, Kira Niklas, Dr. Julian Stahl",
                  description:
                    "Wie man erfolgreich Arbeitskräfte in Industrie, Handel und Dienstleistung gewinnt. Ein umfassender Überblick über die Situation sowie Praxistipps für den Recruitingalltag.",
                  coverImage: "/books/blue-hiring-cover.jpg",
                  coverOpacity: "opacity-[0.13]",
                  coverSize: "bg-cover",
                  downloadUrl: "/books/Blue_Hiring.pdf",
                  externalUrl: "",
                  extraSpace: true,
                },
                {
                  title: "Chefsachen – Die Schöne und das Biest?",
                  authors: "Dr. Margret Klinkhammer",
                  description:
                    "Über das Alte und Neue und scheinbare Gegensätze – ein Beitrag im RKW-Managementbrief für alle, die mittelständische Unternehmen führen und entwickeln wollen.",
                  coverImage: "",
                  coverOpacity: "",
                  coverSize: "",
                  downloadUrl: "/books/RKW-Managementbrief_Chefsachen_2_2018.pdf",
                  externalUrl: "",
                  extraSpace: false,
                },
                {
                  title: "Cooperative Praxis – eine neue Form der Konfliktregelung",
                  authors: "Dr. Margret Klinkhammer, Dr. Gisela Mähler, Dr. Hans-Georg Mähler",
                  description:
                    "Bei der Cooperativen Praxis arbeiten Anwälte, Fachexperten und Coaches zusammen und übernehmen als Team die Gesamtverantwortung für den Prozess der Konfliktlösung.",
                  coverImage: "",
                  coverOpacity: "",
                  coverSize: "",
                  downloadUrl: "/books/Cooperative_Praxis_Konfliktregelung.pdf",
                  externalUrl: "",
                  extraSpace: false,
                },
                {
                  title: "Der Einsatz von Collaboration-Tools im Change-Prozess",
                  authors: "Margret Klinkhammer, Stephan Rathgeber, Michael Schneider, Christina Bruns",
                  description:
                    "Auszug aus dem Werk \u201EChange happens\u201C \u2013 \u00fcber den gezielten Einsatz von Collaboration-Tools zur Unterst\u00fctzung von Ver\u00e4nderungsprozessen in Organisationen.",
                  coverImage: "",
                  coverOpacity: "",
                  coverSize: "",
                  downloadUrl: "/books/Collaboration-Tools.pdf",
                  externalUrl: "",
                  extraSpace: false,
                },
              ].map((pub, i) => (
              <motion.div key={i} variants={fadeInUp} className="h-full">
                  <Card className="hover:shadow-md transition-shadow p-8 flex flex-col min-h-[220px] relative overflow-hidden h-full">
                    {pub.coverImage && (
                      <div
                        className={`absolute inset-0 ${pub.coverSize} bg-center ${pub.coverOpacity} pointer-events-none`}
                        style={{ backgroundImage: `url(${pub.coverImage})` }}
                      />
                    )}
                    <div className="relative z-10 flex flex-col flex-1">
                    <BookOpen className="h-5 w-5 text-primary/40 mb-3" />
                    <h3 className="text-base font-serif font-semibold text-foreground mb-1 leading-snug">
                      {pub.title}
                    </h3>
                    {pub.authors && <p className="text-xs text-muted-foreground mb-2">{pub.authors}</p>}
                    <p className="text-sm text-muted-foreground flex-1">{pub.description}</p>
                    {(pub.downloadUrl || pub.externalUrl) && (
                      <div className="mt-4 pt-4 border-t border-border flex gap-4">
                        {pub.downloadUrl && (
                          <a
                            href={pub.downloadUrl}
                            download
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                          >
                            <Download className="h-3.5 w-3.5" /> PDF Download
                          </a>
                        )}
                        {pub.externalUrl && (
                          <a
                            href={pub.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                          >
                            <ExternalLink className="h-3.5 w-3.5" /> Zum Buch
                          </a>
                        )}
                      </div>
                    )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <SectionDivider />

        {/* ═══════════════════════════════════════════════════════ */}
        {/* PARTNERNETZWERK */}
        {/* ═══════════════════════════════════════════════════════ */}
        <section id="partnernetzwerk" className="py-20 md:py-28 bg-surface scroll-mt-14">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
              className="max-w-3xl mx-auto text-center mb-10"
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm font-semibold uppercase tracking-widest text-primary mb-3"
              >
                Gemeinsam stark
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-6">
                Partnernetzwerk
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-muted-foreground leading-relaxed">
                Gemeinsam stark – unsere Partner für nachhaltige Führungsentwicklung.
              </motion.p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {partners.map((p) => (
                <motion.div
                  key={p.name}
                  variants={fadeInUp}
                  className={`group flex min-h-[180px] items-center gap-4 overflow-hidden rounded-lg border p-6 shadow-sm transition-shadow hover:shadow-md ${p.dark ? "border-foreground/20 bg-foreground" : "bg-card text-card-foreground"}`}
                >
                  <div className="min-w-0 flex-1">
                    <h3 className={`mb-2 break-words hyphens-auto font-serif text-xl font-semibold ${p.dark ? "text-primary-foreground" : "text-foreground"}`}>
                      {p.name}
                    </h3>
                    <p className={`text-sm ${p.dark ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{p.branche}</p>
                    {p.url && (
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                      >
                        Website besuchen <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                  {p.logo &&
                    (() => {
                      const logo = (
                        <img
                          src={p.logo}
                          alt={`${p.name} Logo`}
                          loading="lazy"
                          className={p.logoClass ?? "h-14 w-auto max-w-[108px] shrink-0 object-contain"}
                        />
                      );
                      return p.url ? (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Zur Website von ${p.name}`}
                          className="shrink-0 self-center rounded transition-transform duration-200 hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          {logo}
                        </a>
                      ) : (
                        logo
                      );
                    })()}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Bildtrenner zwischen Partner & Referenzen */}
        <section className="relative w-full overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8 }}
            className="w-full relative"
          >
            <img
              src={burkhardStephanConversation}
              alt="Burkhard und Stephan Rathgeber im Gespräch"
              className="w-full h-auto"
            />
          </motion.div>
        </section>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* REFERENZEN */}
        {/* ═══════════════════════════════════════════════════════ */}
        <section id="partner" className="py-20 md:py-28 bg-background scroll-mt-14">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}
              className="max-w-3xl mx-auto text-center mb-10"
            >
              <motion.p
                variants={fadeInUp}
                className="text-sm font-semibold uppercase tracking-widest text-primary mb-3"
              >
                Unsere Kunden
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-6">
                Referenzen
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-muted-foreground leading-relaxed">
                <motion.p variants={fadeInUp} className="text-lg text-muted-foreground leading-relaxed">
                  Was unsere Kunden über die Zusammenarbeit sagen.
                </motion.p>
              </motion.p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(() => {
                const referenzen = [
                  {
                    person: "1. Ausbildungsjahrgang",
                    schlagworte: "Qualifizierung zur Organisationsentwickler:in",
                    zitat:
                      "Widerstände erlebt – Konflikte ausgetragen – für Überraschungen gesorgt und einen vertrauensvollen Raum für Selbsterfahrung, Entfaltung und Entwicklung gefunden",
                    vollzitat:
                      "Liebe Bettina, liebe Margret, Ihr habt uns im Verlauf der letzten 12 Monate mit jeder Menge neuem Wissen und Hintergrundinformationen ausgestattet, jeder von uns unzählbare Denkanstöße mitgegeben, viele Erkenntnisse ermöglicht und sicherlich eine weiterführende Reise angestoßen.\n\nWir danken Euch von Herzen für Euer Vertrauen, Euren Wissensschatz mit uns zu teilen, und für Euer Zutrauen, dass genau wir die Richtigen dafür sind.\n\nWir sind sehr stolz darauf, Eure erste Generation der Qualifizierung zu sein und wünschen Euch viele weitere gelungene Ausführung.\n\nHerzlich\nAnja Braune – Daniela Rehorn – Doris Kish – Hanka Czichos – Nicola Enderle – Rita Frost",
                  },
                  {
                    firma: "Hays",
                    person: "Dirk Hahn, Global CEO",
                    schlagworte: "B2B Marketing · Social Selling · Digitalstrategie",
                    zitat:
                      "Stephan hat die Organisation nachhaltig bewegt und ein gro\u00DFes St\u00FCck weit digitaler gemacht. Zu nennen sind hier das Social Selling-Programm, der Aufbau der Digital Unit und unsere Digitalstrategie.",
                    vollzitat:
                      "Stephan hat die Organisation nachhaltig bewegt und ein gro\u00DFes St\u00FCck weit digitaler gemacht. Zu nennen sind hier das Social Selling-Programm, der Aufbau der Digital Unit und unsere Digitalstrategie.",
                  },
                  {
                    firma: "SOS-Kinderdorf e.V.",
                    person: "Nadja Nicolay",
                    schlagworte: "Führungskräftetrainingsprogramm",
                    zitat:
                      "Mit Margret Klinkhammer haben wir eine exzellente Trainerin, Supervisorin und Partnerin gewonnen. Es ist Freude und Genuss, auf solch hohem Niveau mit den Kolleg:innen zu arbeiten.",
                    vollzitat:
                      "2023 haben wir in der Personalentwicklung von SOS-Kinderdorf e.V. eine neue Leadership-Qualifizierung für die Position der Einrichtungsleitung eingeführt – zuerst als Pilot und jetzt im Roll-Out. Ziel ist es, Führungskräfte gezielt auf die Anforderungen dieser komplexen Führungsrolle vorzubereiten und sie darin zu stärken, Verantwortung wirksam und reflektiert zu übernehmen.\n\nIn der Qualifizierung erarbeiten wir uns ein systemtheoretisch fundiertes Verständnis von Führung, beschäftigen uns mit organisationalen Mustern und deren De- und Neukonstruktion und widmen uns intensiv dem Umgang mit organisationalen Paradoxien. Unsere Leadership-Qualifizierung bietet Raum für Reflexion, kollegiale Beratung, Erfahrungslernen sowie regional übergreifende Vernetzung.\n\nMit Margret Klinkhammer von Leadership Focus GmbH haben wir eine exzellente Trainerin, Supervisorin und starke Partnerin gewonnen, mit der ich die Qualifizierung umsetze. Es ist Freude und Genuss, auf solch hohem Niveau mit den Kolleg*innen zu arbeiten. Ein herzliches Danke!",
                  },
                  {
                    firma: "SOS Kinderdorf e.V.",
                    person: "Lydia S.",
                    schlagworte: "Raus aus der Komfortzone · Systemisches Denken · Peergruppe",
                    zitat:
                      "Ein Austausch auf hochkomplexer Ebene mit Gleichgesinnten aus Kinderdörfern aus ganz Deutschland, die bereit waren immer wieder ihre Komfortzone zu verlassen und sich Herausforderungen zu stellen – das hat mich wirklich weitergebracht.",
                    vollzitat:
                      "Raus aus der Komfortzone\n\nHerausforderung gesucht? Das Hirn im systemischen Denken trainieren und damit dein Umfeld neu kennenlernen und das über ein Jahr lang?\n\nMit Präsenzterminen in Augsburg und Berlin, Onlineterminen, persönlichen Coachings und Peergruppenerfahrung kann ich auf ein Jahr voll wertvoller Erfahrung zurückschauen. Ein Austausch auf hochkomplexer Ebene mit Gleichgesinnten aus Kinderdörfern aus ganz Deutschland, die bereit waren immer wieder ihre Komfortzone zu verlassen und sich Herausforderungen zu stellen – das hat mich wirklich weitergebracht.",
                  },
                  {
                    firma: "DR-WALTER",
                    person: "Laura Suhr, CHRO",
                    schlagworte: "Corporate Influencing · Employer Branding",
                    zitat:
                      "Ich habe Stephan im Rahmen unseres Corporate Influencer Programms kennengelernt \u2013 und es war sofort klar: Hier passiert etwas Besonderes. Er bringt nicht nur eine ansteckende Energie mit, sondern auch ein tiefes Verst\u00E4ndnis daf\u00FCr, wie Unternehmen ihre Sichtbarkeit und Attraktivit\u00E4t steigern k\u00F6nnen.",
                    vollzitat:
                      "Ich habe Stephan im Rahmen unseres Corporate Influencer Programms kennengelernt \u2013 und es war sofort klar: Hier passiert etwas Besonderes. Er bringt nicht nur eine ansteckende Energie mit, sondern auch ein tiefes Verst\u00E4ndnis daf\u00FCr, wie Unternehmen ihre Sichtbarkeit und Attraktivit\u00E4t steigern k\u00F6nnen.",
                  },
                  {
                    firma: "Vorstand Beratungsunternehmen",
                    person: "Annette Moosmüller",
                    schlagworte: "Supervision von Coaches und Berater:innen",
                    zitat: "Margret Klinkhammer ist unser \u201EQualit\u00E4tsgate\u201C.",
                    vollzitat:
                      "Margret Klinkhammer ist unser \u201EQualit\u00E4tsgate\u201C. Sie ist klar in ihren Rollen, hat einen hohen Professionalit\u00E4tsanspruch und h\u00E4lt uns und sich immer wieder den Spiegel vor, damit wir unser Denken und Handeln reflektieren.",
                  },
                  {
                    firma: "Caritas",
                    person: "Wolfgang Schn\u00F6rr, Vorstand",
                    schlagworte: "New Hiring · Employer Branding · Onboarding",
                    zitat:
                      "Stephan hat uns mit seinem New Hiring Wissen nicht nur geholfen, das Thema als ganzheitlichen Prozess vom Onboarding bis zum Offboarding zu verstehen. Er hat uns vermittelt, wie wir es in unserem caritativen Auftrag neu mit Leben füllen!",
                  },
                  {
                    firma: "Referatsleiterin, evang. Kirche",
                    person: "G. S.",
                    schlagworte: "Change Prozessberatung",
                    zitat:
                      "Professionelle und flexible Begleitung in einem komplexen Ver\u00E4nderungsprozess durch die gemeinsame Entwicklung praxisnaher und direkt umsetzbarer Vorgehensweisen.",
                    vollzitat:
                      "Wir brauchten Beratung in einem Ver\u00E4nderungsprojekt mit sehr schwierigen Rahmenbedingungen \u2013 und bekamen Unterst\u00FCtzung mit Rat und Tat \u2013 sehr professionell, einf\u00FChlsam und aufmerksam \u2013 ohne das \u201Egro\u00DFe Ganze\u201C aus den Augen zu verlieren. Sehr geholfen hat uns, dass immer flexibel und flink auf ver\u00E4nderte Situationen eingegangen wurde und wir \u2013 h\u00E4ufig gemeinsam \u2013 durchweg sehr praxisnahe und umsetzbare Vorgehensweisen entwickeln konnten. Jederzeit gerne wieder!",
                  },
                  {
                    firma: "TNG Technology Consulting GmbH",
                    person: "Gerhard M\u00FCller, Partner & Gesch\u00E4ftsf\u00FChrer",
                    schlagworte: "Organisationsentwicklung",
                    zitat:
                      "Langj\u00E4hrige Begleitung bei strategischen Fragestellungen, Supervision interner Ansprechpartner sowie individuelles F\u00FChrungskr\u00E4fte-Coaching auf Basis h\u00F6chster menschlicher und fachlicher Expertise.",
                    vollzitat:
                      "Mit Frau Dr. Klinkhammer arbeitet TNG Technology Consulting nun schon seit mehreren Jahren sehr erfolgreich zusammen. Dabei konnten wir in verschiedenen Situationen profitieren: Von der Begleitung von TNG bei strategischen Fragestellungen mit Partnern und Principals, bis \u00FCber die Strukturierung sowie Begleitung (Supervision) der \u201EAnsprechpartner f\u00FCr Sorgen am Arbeitsplatz\u201C / ASAPs, der Reflexion/Hilfe bei herausfordernden Lebenssituationen, bis hin zu auch direktem pers\u00F6nlichen Austausch mit mir \u00FCber mehrere Jahre.\n\nBesonders hervorzuheben ist die angenehme pers\u00F6nliche und menschliche Art, gepaart mit der langj\u00E4hrigen Erfahrung, und damit der wunderbaren Herausarbeitung der besonders wichtigen Fragestellungen, an denen dann intensiv erfolgreich gemeinsam gearbeitet wird. Ich kann Frau Dr. Klinkhammer nur w\u00E4rmstens empfehlen!",
                  },
                  {
                    firma: "Netzwerk \u201EKonfliktkultur und Mobbing in Arbeitswelt & Schule\u201C",
                    person: "Susanne Schneider, Sprecherin",
                    schlagworte: "Workshop \u201EDie Kunst des Konflikts\u201C",
                    zitat:
                      "Konzeption und Durchf\u00FChrung eines Fachtags zum Thema Konfliktkultur: komplexe systemtheoretische Zusammenh\u00E4nge wurden f\u00FCr ein breites Publikum didaktisch brillant und unmittelbar praxisnah erlebbar gemacht.",
                    vollzitat:
                      "Um den Konflikt aus seiner verstaubten Ecke zu holen und ihm mit einer neuen Haltung begegnen zu k\u00F6nnen, veranstaltete das Netzwerk \u201EKonfliktkultur und Mobbing in Arbeitswelt und Schule\u201C einen Fachtag f\u00FCr die interessierte \u00D6ffentlichkeit und f\u00FCr Fachkolleg*innen. Das Thema war \u201EDie Kunst des Konflikts oder: \u00FCber die N\u00FCtzlichkeit von Konflikten\u201C, basierend auf dem gleichnamigen Buch von Klaus Eidenschink. Bei unserer Suche nach Referierenden f\u00FCr diesen Tag sind wir auf Frau Dr. Klinkhammer gesto\u00DFen. Vom ersten Augenblick des Kontaktes \u00FCber die Anfrage, die Auftragsgestaltung, das Herausarbeiten des sinnvoll M\u00F6glichen bis hin zur Durchf\u00FChrung gestaltete sich die Zusammenarbeit mit ihr zur unserer gr\u00F6\u00DFten Zufriedenheit und Begeisterung. Frau Dr. Klinkhammer ist es gelungen, einem gro\u00DFen und sehr bunt gemischten Publikum die kompliziertesten Zusammenh\u00E4nge klar und verst\u00E4ndlich zu vermitteln. In ihrer hochkompetenten und gleichzeitig \u00FCberaus menschlichen Art hat sie die anspruchsvolle Logik der Systemtheorie mit Pr\u00E4gnanz, didaktisch wertvoll aufgearbeitet und gleichzeitig lebendig, praxisnah dem Auditorium zur Verf\u00FCgung gestellt.\n\nSystemtheorie zum Anfassen! Herrlich! In gut nachvollziehbaren theoretischen Teilschritten, durchbrochen von praktischen Umsetzungsaufgaben in kleinen Gruppen, hat Frau Dr. Klinkhammer zusammen mit ihrem Team von Frau Honsu, Herrn Rathgeber und Frau Michels ein geniales Konzept geschaffen und den Tag gestaltet. Unterst\u00FCtzt von den ansprechenden, fein gestalteten Handouts und konkreten \u00DCbungsauftr\u00E4gen konnten die Teilnehmenden das Geh\u00F6rte unmittelbar in Kleingruppen in die Praxis transferieren und ausprobieren. Insgesamt k\u00F6nnen wir von einer sehr inspirierenden, konstruktiven und kooperativen, kreativen Zusammenarbeit sprechen. Herzlichen Dank, liebe Frau Dr. Klinkhammer!",
                  },
                  {
                    firma: "kreuzwerker GmbH",
                    person: "Kristine Jetzke, Gesch\u00E4ftsf\u00FChrerin",
                    schlagworte: "Organisationsentwicklung & Coaching",
                    zitat:
                      "Ganzheitliche Begleitung eines internationalen Reorganisations- und Strategieprojekts: Von der Beratung der F\u00FChrungsgremien \u00FCber die Moderation l\u00E4nder\u00FCbergreifender Workshops bis zur Neugestaltung globaler Gesch\u00E4ftsprozesse.",
                    vollzitat:
                      "\u201EAls Coach und Organisationsentwicklerin begleitete Margret Klinkhammer uns kreuzwerker intensiv w\u00E4hrend unseres Strategie- und Reorganisationsprojekts 2022/2023. Mit ihrer Unterst\u00FCtzung haben wir die Kommunikation des Betriebs\u00FCbergangs und unserer neuen strategischen Ausrichtung erarbeitet und umgesetzt. Margret begleitete sowohl unser Partnergremium als auch die neue Gesch\u00E4ftsf\u00FChrung, so dass wir gemeinsam Interessen und zuk\u00FCnftige Arbeitsweisen aushandeln konnten. Zudem moderierte sie achtsam und zielorientiert unsere standort- und l\u00E4nder\u00FCbergreifenden Leadership-Workshops. Unsere kreuzwerker Unternehmenskultur konnte so gemeinsam gefestigt werden. Margret moderierte ebenfalls unsere Workshops zur Neugestaltung unserer Gesch\u00E4ftsprozesse und der internationalen Zusammenarbeit.\n\nWir danken Dir, Margret, sehr f\u00FCr Deine einf\u00FChlsame und fokussierte Unterst\u00FCtzung sowie f\u00FCr Dein offenes und konstruktives Feedback.\n\nWir freuen uns auf die weitere Zusammenarbeit mit Dir und empfehlen Dich sehr gerne weiter.\u201C",
                  },
                  {
                    firma: "Kirchliche Organisationsberatung",
                    person: "Dr. Judith Müller",
                    schlagworte: "Gruppensupervision",
                    zitat:
                      "Neugierig, empathisch, methodisch vielfältig und immer hoch professionell, analysestark und zielgerichtet. Es war uns eine Freude.",
                    vollzitat:
                      "Als Arbeitsgemeinschaft kirchlicher Gemeinde- und Organisationsberater*innen haben wir in den 2,5 Jahren, in denen Frau Dr. Klinkhammer uns als Supervisorin begleitet hat, reich profitiert. Das Wagnis, die Fremdheit der Systemlogiken (Wirtschaftsunternehmen vs. Kirche) zum Lernen zu nutzen, hat sich gelohnt und ist geglückt, weil sie sich als ganze Person darauf eingelassen hat: neugierig, empathisch, methodisch vielfältig und immer hoch professionell, analysestark und zielgerichtet. Es war uns eine Freude.",
                  },
                  {
                    firma: "Technische Hochschule Ulm",
                    person: "Iris Teicher, Kanzlerin / Prof. Dr. Ing. Robert Watty, Dekan",
                    schlagworte: "Product Owner Forschung",
                    zitat:
                      "Begleitung eines BMBF-geförderten Forschungsprojekts als Product Ownerin: Brückenbau zwischen agilen und klassischen Strukturen sowie gezielte Förderung von Selbstorganisation und wertschätzender Teamarbeit.",
                    vollzitat:
                      "Als Product Ownerin hat Margret unser vom BMBF gefördertes Forschungsprojekt \u201ETHUProf4.0.\u201C in einer entscheidenden Phase begleitet. Durch ihr Einfühlungsvermögen und ihr zielorientiertes Vorgehen gelang es ihr vom ersten Moment an, gleichermaßen Vertrauen zu Stakeholdern und Projektmitgliedern aufzubauen. Gemeinsam wurden Brücken geschlagen und Übersetzungen erarbeitet zwischen \u201Eneu und alt\u201C, \u201Eagil und stabil\u201C, \u201EWaterfall-Vorgehen und iterativem, experimentellen Vorgehen\u201C. Es war eine Freude zu sehen, wie sich die Professionalität der Einzelnen im Team entfalten konnte zu einem größeren Ganzen.\n\nHerzlichen Dank Dir, Margret, für Deine wertvollen Beiträge und Anstöße zur Gestaltung einer wertschätzenden Teamarbeit sowie zur Rahmensetzung und Unterstützung unserer Selbstorganisation.",
                  },
                  {
                    firma: "Bildungswerk ver.di in Bayern e.V.",
                    person: "Nicole Kn\u00FCttel",
                    schlagworte: "Workshop \u201EDie Kunst des Konflikts\u201C",
                    zitat:
                      "Mit ihrer inspirierenden Art und gro\u00DFen Erfahrung gelingt es Frau Dr. Klinkhammer, Teilnehmer f\u00FCr das Thema Konfliktmanagement zu begeistern und ihnen wertvolle Werkzeuge an die Hand zu geben, um Konflikte als echte Chance zur Weiterentwicklung zu nutzen.",
                    vollzitat:
                      "Im Rahmen einer Bildungsveranstaltung zum Thema \u201EDie Kunst des Konflikts\u201C, des Bildungswerkes der Gewerkschaft ver.di in Bayern e. V., haben wir Frau Dr. Klinkhammer als Referentin gewinnen k\u00F6nnen.\n\nKonflikte geh\u00F6ren zum Leben dazu und sind oft unvermeidlich, doch sie k\u00F6nnen auch Chancen zur Weiterentwicklung und zum besseren Verst\u00E4ndnis bieten. Frau Dr. Klinkhammer hat den Teilnehmer*innen wichtige Einblicke in die verschiedenen Facetten von Konflikten gegeben und gleichzeitig wertvolle Tools bereitgestellt, um Konflikte zu regulieren. Frau Dr. Klinkhammer zeichnet sich durch fundierte Fachkenntnisse, ihre didaktische Kompetenz und ihre gro\u00DFe Erfahrung im Umgang mit konfliktbezogenen Themen aus. Mit ihrer inspirierenden Art gelingt es ihr, komplexe Zusammenh\u00E4nge verst\u00E4ndlich zu vermitteln und die Teilnehmer*innen f\u00FCr das Thema zu begeistern. Wir k\u00F6nnen sie uneingeschr\u00E4nkt als Expertin auf diesem Gebiet empfehlen.",
                  },
                  {
                    firma: "Bereichsleitung Versicherungsunternehmen",
                    person: "M.B & W.G.",
                    schlagworte: "Workshop Moderation",
                    zitat:
                      "Liebe Margret, vielen Dank f\u00FCr die Moderation unseres zweit\u00E4gigen interaktiven Workshops. Zum Gelingen trugen insbesondere deine langj\u00E4hrige Managementerfahrung sowie deine Flexibilit\u00E4t, sich auf ver\u00E4ndernde Rahmenbedingungen einzustellen, bei. Zudem war das Design der beiden Tage eine optimale Mischung aus theoretischem Input, aktiver Diskussionsrunden und direkter Erfahrung von Teamdynamiken. Durch deinen Input werden wir zuk\u00FCnftig ein noch schlagkr\u00E4ftigeres Team sein.",
                    vollzitat:
                      "Liebe Margret, vielen Dank f\u00FCr die Moderation unseres zweit\u00E4gigen interaktiven Workshops. Zum Gelingen trugen insbesondere deine langj\u00E4hrige Managementerfahrung sowie deine Flexibilit\u00E4t, sich auf ver\u00E4ndernde Rahmenbedingungen einzustellen, bei. Zudem war das Design der beiden Tage eine optimale Mischung aus theoretischem Input, aktiver Diskussionsrunden und direkter Erfahrung von Teamdynamiken. Durch deinen Input werden wir zuk\u00FCnftig ein noch schlagkr\u00E4ftigeres Team sein.",
                  },
                  {
                    firma: "Caritasverband der Erzdi\u00F6zese M\u00FCnchen & Freising e.V.",
                    person: "Gabriele Stark-Angermeier, Vorstand",
                    schlagworte: "Projektbegleitung und Organisationsentwicklung",
                    zitat:
                      "Durch die konsequente Begleitung ist bei der Caritas etwas wirklich Neues entstanden: Ein Projekt, bei dem neben den Inhalten vor allem die Entwicklung der Pers\u00F6nlichkeiten im Vordergrund stand \u2013 so machen Lernen und gemeinsames Wachstum Freude.",
                    vollzitat:
                      "Vielen Dank f\u00FCr die konstante und sehr konsequente Begleitung des Projekts zur \u201EOptimierung der Verpflegung im Caritasverband M\u00FCnchen-Freising\u201C. Hier ist wirklich etwas Neues f\u00FCr die Caritas entstanden. Auch wenn die Fr\u00FCchte erst etwas sp\u00E4ter gegenst\u00E4ndlich werden, da die Produktionsk\u00FCche noch gebaut werden muss, so sind doch Saat und Pflanze gut gesetzt worden. Beides kann nun stabil weiter wachsen.\n\nIm Projekt stand nicht nur der Inhalt im Vordergrund, sondern auch die Entwicklung der einzelnen Pers\u00F6nlichkeiten. Das war genauso wie in den anderen Projekten und Gremien, die Sie mit begleitet und auch angeschoben haben. So machen Lernen und Entwicklung Spa\u00DF!",
                  },
                  {
                    firma: "The GXC Management Team",
                    person: "Prof. Dr. Gudrun Socher & Dr. Audrey Stolze",
                    schlagworte: "Agiles Coaching in der Lehre",
                    zitat:
                      "In unserem internationalen Innovations-Programm schuf Margret als systemischer und agiler Coach wertvolle R\u00E4ume f\u00FCr Reflexion und Teambuilding: Eine Begleitung, die sowohl die pers\u00F6nliche Entwicklung der Studierenden als auch die Teamdynamik und die p\u00E4dagogische Qualit\u00E4t sp\u00FCrbar bereicherte.",
                    vollzitat:
                      "We engaged Margret as a systemic and agile coach on our GXC program to enrich our students\u2019 learning experience when working in remote international interdisciplinary student teams to solve innovation challenges proposed by partner companies. Margret enabled team building and provided our student teams a private environment for discussion and reflection, effectively syncing the coaching and innovation processes.\n\nOur course evaluation showed the value added by Margret. Our students positively accessed the coaching experience as enriching to them personally, and to their team dynamic. At the same time, our periodical exchange with Margret helped us improve our course\u2019s pedagogical scaffolding to provide our students a \u201Ereal project\u201C experience.",
                  },
                  {
                    firma: "TU M\u00FCnchen, Lehrstuhl Finanzmathematik",
                    person: "Prof. Dr. Rudi Zagst",
                    schlagworte: "Summer School, Elitestudiengang Finanz- und Informationsmanagement",
                    zitat:
                      "Das Feedback der Studenten nach allen drei Veranstaltungen mit Dir war grandios. Wir freuen uns sehr, dass wir die letzten drei Jahre gemeinsam mit Dir bestreiten und von Deiner Erfahrung profitieren durften.",
                    vollzitat:
                      "Liebe Margret,\n\n...das Feedback der Studenten nach allen drei Veranstaltungen mit Dir war grandios.\n\nWir freuen uns sehr, dass wir die letzten drei Jahre gemeinsam mit Dir bestreiten und von Deiner Erfahrung profitieren durften. Ich m\u00F6chte mich daf\u00FCr, auch im Namen meines Kollegen Uli Buhl, den Assistenten und Studenten ganz herzlich bedanken.",
                  },
                  {
                    firma: "BP-EMEA",
                    person: "Matthias Krause-Uhrmann, Einkaufsleiter",
                    schlagworte: "Coaching Bereichsleitung",
                    zitat:
                      "I would like to thank Margret sincerely for her great professional, insightful, sensitive but brutally open and honest feedback and support, that has contributed so much to my development at BP over the last 12 months.",
                    vollzitat:
                      "Last but not least, I would like to thank Margret sincerely and officially for her great professional, insightful, sensitive but brutally open and honest feedback and support, that has contributed so much to my development at BP over the last 12 months. Margret is very experienced and perfectly suited individual in this so important role. I will strongly recommend her to BP and other coaches with a need in her area and will be happy to act as reference whenever requested.",
                  },
                  {
                    firma: "Internationaler Versicherungskonzern",
                    person: "B.Z.",
                    schlagworte: "Teamentwicklung Betriebsrat",
                    zitat:
                      "Noch am Abend waren alle in gel\u00F6ster Stimmung und voll des Lobes f\u00FCr Ihr \u00FCberragendes Einf\u00FChlungsverm\u00F6gen. Gerne kommen wir wieder auf Sie zur\u00FCck und werden uns erlauben, Sie w\u00E4rmstens weiter zu empfehlen.",
                    vollzitat:
                      "Liebe Frau Dr. Klinkhammer,\n\nich m\u00F6chte mich noch einmal im Namen aller Kolleginnen und Kollegen recht herzlich f\u00FCr den gestrigen Tag bedanken! Noch am Abend waren alle in gel\u00F6ster Stimmung und voll des Lobes f\u00FCr Ihr \u00FCberragendes Einf\u00FChlungsverm\u00F6gen.\n\nGerne kommen wir wieder auf Sie zur\u00FCck und werden uns auch erlauben, Sie w\u00E4rmstens weiter zu empfehlen.",
                  },
                  {
                    firma: "Technologieunternehmen",
                    person: "A.R., Leitender Angestellter",
                    schlagworte: "Coaching Projektleiter & Change Manager",
                    zitat:
                      "In einer vertrauensvollen Atmosph\u00E4re verbindet Frau Dr. Klinkhammer systematische Faktenarbeit mit der wichtigen emotionalen Perspektive \u2013 man verl\u00E4sst jede Sitzung mit einem konkreten Paket an Aktionen, die bei konsequenter Anwendung sicher zum Erfolg f\u00FChren.",
                    vollzitat:
                      "Bei einer offenen und vertrauensvollen Atmosph\u00E4re ist es mir leicht gefallen, mich zu \u00F6ffnen und am Kern zu arbeiten. Fr. Dr. Klinkhammer hat eine sehr systematische Art, das Coaching durchzuf\u00FChren. Eine Art, die zum einen an den Fakten entlang arbeitet, um wertvolle Hinweise, Sichtweisen und Empfehlungen mitzugeben, aber die auch die ebenso wichtige emotionale Perspektive parallel dazu nicht aus den Augen verliert. Zum Schluss verl\u00E4sst man die Session immer mit einen Paket an Aktionen, die bei konsequenter Anwendung auch erfolgreich sind. Besonders gesch\u00E4tzt habe ich, dass selbst bei 2 Monaten Abstand zwischen den Sessions der Sachverhalt pr\u00E4sent war und keine Zeit mit Wiederholen verloren gegangen ist.",
                  },
                  {
                    firma: "Bereichsleitung Bank",
                    person: "I.S.",
                    schlagworte: "Workshop mit F\u00FChrungsteam",
                    zitat:
                      "Professionelle und engagierte F\u00FChrung in unserem zweit\u00E4gigen Workshop \u2013 toll auf das Team eingegangen und zu intensiven Diskussionen und Reflexionen angeregt.",
                    vollzitat:
                      "Liebe Margret,\n\n... Dir nochmal ein ganz herzliches Dankesch\u00F6n f\u00FCr Deine professionelle und engagierte F\u00FChrung in unseren 2 Tagen. Du bist toll auf das Team eingegangen und hast uns sehr gut zu intensiven Diskussionen und ... Reflexionen angeregt.",
                  },
                ];
                return referenzen.map((ref, i) => (
                  <motion.div
                    key={i}
                    variants={fadeInUp}
                    className={`rounded-lg border bg-card text-card-foreground shadow-sm hover:shadow-md transition-shadow p-8 flex flex-col min-h-[220px] ${ref.vollzitat ? "cursor-pointer" : ""}`}
                    onClick={() => ref.vollzitat && setSelectedReferenz(ref)}
                  >
                    <Quote className="h-5 w-5 text-primary/40 mb-3" />
                    <p className="text-sm text-foreground/80 italic leading-relaxed flex-1">„{ref.zitat}"</p>
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="font-serif font-semibold text-foreground text-sm">{ref.person}</p>
                      {ref.firma && <p className="text-xs text-muted-foreground">{ref.firma}</p>}
                      <p className="text-xs text-primary/70 mt-1">{ref.schlagworte}</p>
                      {ref.vollzitat && <p className="text-xs text-primary mt-2">Mehr lesen →</p>}
                    </div>
                  </motion.div>
                ));
              })()}
            </div>
          </div>
        </section>

        <Dialog open={!!selectedReferenz} onOpenChange={(open) => !open && setSelectedReferenz(null)}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-serif">{selectedReferenz?.person}</DialogTitle>
              <DialogDescription>{selectedReferenz?.schlagworte}</DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <Quote className="h-5 w-5 text-primary/40 mb-3" />
              {selectedReferenz?.vollzitat?.split("\n\n").map((paragraph, i) => (
                <p key={i} className="text-sm text-foreground/90 leading-relaxed mb-3 whitespace-pre-line">
                  {paragraph}
                </p>
              ))}
            </div>
          </DialogContent>
        </Dialog>



        {/* CTA / Kontakt */}
        <section id="kontakt" className="py-20 md:py-28 bg-primary-light scroll-mt-14">
          <div className="container text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                Bereit für den nächsten Schritt?
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
                Lassen Sie uns gemeinsam herausfinden, wie wir Ihre Führung und Organisation stärken können.
              </motion.p>
              <motion.div variants={fadeInUp}>
                <Button asChild size="lg" className="text-base px-10">
                  <a href="mailto:kontakt@leadership-focus.de">
                    kontakt@leadership-focus.de
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* BLOG TEASER */}
        {/* ═══════════════════════════════════════════════════════ */}
        {/* ═══════════════════════════════════════════════════════ */}
        {/* BLOG TEASER */}
        {/* ═══════════════════════════════════════════════════════ */}
        <section id="blog-page" className="py-20 md:py-28 bg-surface scroll-mt-14">
          <div className="container">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
              <motion.p variants={fadeInUp} className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">
                Aktuelles
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-4">
                Blog
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Impulse, Erfahrungen und Perspektiven aus unserer Arbeit mit Führungskräften und Organisationen.
              </motion.p>
            </motion.div>

            {latestPosts.length > 0 && (
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10"
              >
                {latestPosts.map((p) => (
                  <BlogCard key={p.slug} post={p} compact />
                ))}
              </motion.div>
            )}

            <div className="text-center">
              <Button asChild size="lg" variant="outline" className="text-base px-10">
                <Link to="/blog">
                  Alle Artikel
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
};

export default Index;
